import { useEffect, useState } from 'react';
import { AlertCircle, Check, Loader2, Plus, ShieldCheck, X, XCircle } from 'lucide-react';
import {
  INTERVENTION_TYPES_VALIDATION,
  type InterventionStatutValidation,
  type InterventionTypeValidation,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { ModalPortal } from '../ModalPortal';
import { StatutValidationBadge, typeValidationLabel } from './badges';

/**
 * Panneau "Validations" du drawer Intervention (US-INT-04).
 *
 *  - Liste les validations multi-niveaux demandées.
 *  - Bouton "Demander une validation" → mini-modal type + valideur.
 *  - Approuver / Rejeter inline pour les validations qui me sont assignées.
 */

interface ValidationRow {
  id: string;
  intervention_id: string;
  type_validation: InterventionTypeValidation;
  statut_validation: InterventionStatutValidation;
  valideur_id: string;
  date_reponse: string | null;
  commentaire: string | null;
  created_at: string;
  valideur: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
}

interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export function ValidationsPanel({
  interventionId,
  onRefresh,
}: {
  interventionId: string;
  onRefresh?(): void;
}) {
  const { user } = useAuth();
  const [items, setItems] = useState<ValidationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [respondTo, setRespondTo] = useState<{
    row: ValidationRow;
    approve: boolean;
  } | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: ValidationRow[] }>(
        `/interventions/${interventionId}/validations`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Demander une validation
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Aucune validation demandée pour cette intervention.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((v) => {
            const isAssignedToMe = user?.id === v.valideur_id;
            const canRespond = isAssignedToMe && v.statut_validation === 'en_attente';
            return (
              <li key={v.id} className="rounded-lg border border-gray-200 p-3 bg-white text-sm">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {typeValidationLabel(v.type_validation)}
                      </span>
                      <StatutValidationBadge statut={v.statut_validation} />
                    </div>
                    <p className="text-xs text-gray-600">
                      Valideur :{' '}
                      <span className="font-medium">
                        {v.valideur ? `${v.valideur.first_name} ${v.valideur.last_name}` : '—'}
                      </span>
                      {v.valideur && (
                        <span className="text-gray-400 ml-1">({v.valideur.email})</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Demandée le {new Date(v.created_at).toLocaleString()}
                      {v.date_reponse &&
                        ` · répondue le ${new Date(v.date_reponse).toLocaleString()}`}
                    </p>
                    {v.commentaire && (
                      <p className="mt-2 text-xs text-gray-700 italic border-l-2 border-gray-200 pl-2">
                        "{v.commentaire}"
                      </p>
                    )}
                  </div>
                  {canRespond && (
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={() => setRespondTo({ row: v, approve: true })}
                        className="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium"
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Approuver
                      </button>
                      <button
                        onClick={() => setRespondTo({ row: v, approve: false })}
                        className="inline-flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        Rejeter
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {createOpen && (
        <CreateValidationModal
          interventionId={interventionId}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refresh();
            onRefresh?.();
          }}
        />
      )}

      {respondTo && (
        <RespondValidationModal
          row={respondTo.row}
          approve={respondTo.approve}
          onClose={() => setRespondTo(null)}
          onSaved={() => {
            setRespondTo(null);
            refresh();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}

function CreateValidationModal({
  interventionId,
  onClose,
  onSaved,
}: {
  interventionId: string;
  onClose(): void;
  onSaved(): void;
}) {
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [type, setType] = useState<InterventionTypeValidation>('technique');
  const [valideurId, setValideurId] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valideurId) {
      setErrorMessage('Sélectionnez un valideur.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/${interventionId}/validations`, {
        type_validation: type,
        valideur_id: valideurId,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <ShieldCheck className="w-4 h-4 mr-2 text-primary-600" />
              Demander une validation
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type de validation *
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as InterventionTypeValidation)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                {INTERVENTION_TYPES_VALIDATION.map((t) => (
                  <option key={t} value={t}>
                    {typeValidationLabel(t)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Valideur *</label>
              <select
                value={valideurId}
                onChange={(e) => setValideurId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">— Sélectionner —</option>
                {personnels.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.last_name} {p.first_name} — {p.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer la demande
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

export function RespondValidationModal({
  row,
  approve,
  onClose,
  onSaved,
}: {
  row: { id: string; type_validation: InterventionTypeValidation };
  approve: boolean;
  onClose(): void;
  onSaved(): void;
}) {
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!approve && !commentaire.trim()) {
      setErrorMessage('Un commentaire est obligatoire en cas de rejet.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/interventions/validations/${row.id}/repondre`, {
        approve,
        commentaire: commentaire.trim() || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {approve ? 'Approuver la validation' : 'Rejeter la validation'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              Type :{' '}
              <span className="font-medium text-gray-900">
                {typeValidationLabel(row.type_validation)}
              </span>
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Commentaire {approve ? '(optionnel)' : '*'}
              </label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required={!approve}
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white disabled:opacity-50 ${
                  approve ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {approve ? 'Approuver' : 'Rejeter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
